import React, { useRef } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { useAuthenticatedAction } from '@/hooks/useAuthenticatedAction';
import { ReplyToMessage } from '../ReplyToMessage';
import { Loader2, ArrowUp, PaperclipIcon, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useChatHistory } from '../hooks/use-chat-history';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { SignUpForm } from '@/components/auth/SignUpForm'; // Import SignUpForm
import { StopIcon } from '@/components/icons';
import { useIsMobile } from '@/hooks/use-mobile';

interface MessageInputProps {
  rightAccessory?: React.ReactNode;
  footerAddon?: React.ReactNode;
}

export const MessageInput: React.FC<MessageInputProps> = ({
  rightAccessory,
  footerAddon,
}) => {
  const {
    inputMessage,
    setInputMessage,
    sendMessage,
    selectedFiles,
    removeFile,
    handleFileUpload,
    isResponding,
    isUploading,
    replyingToMessage,
    cancelReply,
    stopStreaming,
    currentTaskId,
    activeChat,
    showAuthDialog, // Destructure showAuthDialog
    setShowAuthDialog, // Destructure setShowAuthDialog
  } = useAuthenticatedAction();
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();

  const {
    setNewMessageContentToPreviousUserMessage,
    setNewMessageContentToNextUserMessage
  } = useChatHistory(activeChat?.messages || [], isResponding, setInputMessage);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const clonedFiles = Array.from(files);
      console.log('[MessageInput] handleInputChange cloning files', { count: clonedFiles.length });
      handleFileUpload(clonedFiles);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSend = () => {
    if (!isResponding && (inputMessage.trim() || selectedFiles.length > 0)) {
      sendMessage(inputMessage);
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // On mobile, Enter creates a line break (natural behavior)
    // On desktop, Enter sends the message
    if (e.key === 'Enter' && !isMobile && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    } else if (e.key === "ArrowUp" && e.altKey) {
      if (e.currentTarget.selectionStart === 0 && e.currentTarget.selectionEnd === 0) {
        e.preventDefault();
        setNewMessageContentToPreviousUserMessage();
      }
    } else if (e.key === "ArrowDown" && e.altKey) {
      if (e.currentTarget.selectionStart === inputMessage.length && e.currentTarget.selectionEnd === inputMessage.length) {
        e.preventDefault();
        setNewMessageContentToNextUserMessage();
      }
    }
  };

  const canSendMessage = !isResponding && (inputMessage.trim().length > 0 || selectedFiles.length > 0);
  const isStreaming = isResponding && !!currentTaskId;
  const shouldShowDesktopFooter = !isMobile && (isResponding || Boolean(footerAddon));

  return (
    <Dialog open={showAuthDialog} onOpenChange={setShowAuthDialog}>
      <div className="px-2 py-2 sm:py-4 sm:px-4 pointer-events-none">
        {/* Backdrop gradient for floating effect */}
        <div className="absolute inset-0 from-background/80 via-background/40 to-transparent pointer-events-none" />
        <div className="relative w-full max-w-2xl mx-auto px-4 pointer-events-auto">
          {replyingToMessage && (
            <ReplyToMessage
              message={replyingToMessage}
              onClose={cancelReply}
              isReplying={!!replyingToMessage}
            />
          )}

          {selectedFiles.length > 0 && !replyingToMessage && (
            <div className="mb-2">
              <div className="flex flex-wrap gap-2">
                {selectedFiles.map(file => (
                  <div
                    key={file.id}
                    className="flex items-center p-1.5 rounded-md bg-white/10 backdrop-blur-sm text-xs"
                  >
                    <PaperclipIcon className="h-3.5 w-3.5 mr-1.5 text-neutral-300 flex-shrink-0" />
                    <span
                      className="truncate max-w-[150px] text-neutral-300"
                      title={file.name}
                    >
                      {file.name}
                    </span>
                    <button
                      className="ml-1.5 text-neutral-400 hover:text-red-400 disabled:opacity-50"
                      onClick={() => removeFile(file.id)}
                      disabled={isResponding}
                      title={t('remove_file')}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div
            className={`message-input-wrapper message-input-floating ${replyingToMessage ? 'rounded-b-xl' : 'rounded-xl'
              }`}
          >
            <label
              htmlFor="file-upload"
              className={`p-2.5 transition-colors ${isResponding || isUploading
                  ? 'text-neutral-400 cursor-not-allowed'
                  : 'text-neutral-300 hover:text-white cursor-pointer'
                }`}
              title={isUploading ? t('uploading_files') : t('attach_files')}
            >
              {isUploading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <PaperclipIcon className="h-5 w-5" />
              )}
            </label>
            <input
              id="file-upload"
              type="file"
              ref={fileInputRef}
              className="hidden"
              multiple
              onChange={handleInputChange}
              accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,video/*"
              disabled={isResponding || isUploading}
            />

            <TextareaAutosize
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('send_message_placeholder')}
              className="message-input-textarea"
              disabled={isResponding}
              aria-label={t('send_message_placeholder')}
              rows={1}
              maxRows={6}
            />

            {rightAccessory && (
              <div className="flex items-center px-1.5">
                {rightAccessory}
              </div>
            )}

            {isStreaming ? (
              <button
                className={`p-2 rounded-full transition-colors bg-white/80 text-highlight-foreground`}
                onClick={() => stopStreaming()}
                title={t('stop')}
              >
                <StopIcon className="h-5 w-5" />
              </button>
            ) : (
              <button
                className={`p-2 rounded-full transition-colors ${canSendMessage
                    ? 'bg-highlight/80 text-highlight-foreground hover:bg-highlight'
                    : 'bg-highlight/40 text-highlight-foreground cursor-not-allowed'
                  }`}
                onClick={handleSend}
                disabled={!canSendMessage || isResponding}
                title={canSendMessage ? t('send_message') : t('type_message_or_attach')}
              >
                <ArrowUp className="h-5 w-5" />
              </button>
            )}
          </div>

          {shouldShowDesktopFooter && (
            <div className="mt-2 sm:mt-3 flex justify-center">
              <div className="frosty-glass-footer text-xs text-center text-white flex items-center gap-2 px-4 py-1.5 sm:py-2">
                {isResponding && <span>{t('assistant_thinking')}</span>}
                {footerAddon && (
                  <>
                    {isResponding && <span className="text-white/60">•</span>}
                    {footerAddon}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <DialogContent className="sm:max-w-[425px] p-0">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>
            Create your Lema account
          </DialogTitle>
          <DialogDescription>
            Sign up to continue your conversation and save your progress.
          </DialogDescription>
        </DialogHeader>
        <div className="px-6 pb-6">
          <SignUpForm
            variant="dialog"
            onAuthSuccess={() => setShowAuthDialog(false)}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
